import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { Formik } from 'formik'
import { gql, useMutation } from '@apollo/client'
import { NavLink, useNavigate } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { Input } from 'components/Input'
import { Row } from '@10xLabs/react-web/components/Row'
import { Select } from 'components/v2/Select'
import { safeFormatMessage } from 'utils/intl'
import { Textarea } from 'components/v2/Textarea'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { canRead, isSuperAdmin } from 'features/Users/utils'
import { rewardTypeMessages } from 'features/Loyalty/components/RewardType/messages'
import { schema } from 'features/Loyalty/views/NewReward/schema'
import FormLayout from 'features/App/components/FormLayout'
import Heading from 'components/v2/Heading'
import { ReactElement } from 'react'
import { INewRewardForm } from './types'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  rewardNamePlaceholder: {
    id: 'loyalty.rewards.new.name.placeholder',
    defaultMessage: 'Enter reward name'
  },
  rewardTypePlaceholder: {
    id: 'loyalty.rewards.new.type.placeholder',
    defaultMessage: 'Select reward type'
  },
  rewardTypeDefaultOption: {
    id: 'loyalty.rewards.new.type.default',
    defaultMessage: 'Select option'
  }
})

const CREATE_REWARD = gql`
  mutation CreateLoyaltyReward($input: CreateLoyaltyRewardInput!) {
    createLoyaltyReward(input: $input)
  }
`

const NewRewardView = (): ReactElement => {
  const REWARDS_TYPES_LIST = ['KolorsRewardMVP', 'KolorsGoRewardMVP']
  const intl = useIntl()
  const navigate = useNavigate()
  const login = useLogin()

  const [createReward, { loading, error }] = useMutation(CREATE_REWARD)
  const handleSubmit = async (values: INewRewardForm): Promise<void> => {
    try {
      await createReward({
        variables: {
          input: {
            type: values.rewardType,
            companyID: values.company.id,
            description: values.rewardDescription,
            name: values.rewardName
          }
        }
      })
      navigate(`/loyalty/rewards/`)
    } catch (error) {
      /* empty */
    }
  }
  if (!canRead(FEATURES.BOOKING, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BOOKING} />
  }

  const showCompany = isSuperAdmin(login.accessToken.conditions)

  const initialValues: INewRewardForm = {
    rewardName: '',
    rewardDescription: '',
    rewardType: '',
    company: {
      id: isSuperAdmin(login.accessToken.conditions) ? '' : login.accessToken.companyId || '',
      name: ''
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)} enableReinitialize>
      {(props): ReactElement => {
        const { values, handleChange, handleBlur, touched, errors, handleSubmit, setFieldValue } = props
        const handleCompanyChange = (value: INewRewardForm['company']): void => {
          setFieldValue('company', value)
        }
        return (
          <form autoComplete="false" onSubmit={handleSubmit}>
            <FormLayout.Header>
              <Heading.H3>
                <FormattedMessage id="rewards.new.title" defaultMessage="Rewards" />
              </Heading.H3>
            </FormLayout.Header>
            <FormLayout.Content>
              <FormLayout.CardContent>
                <Row>
                  <Column>
                    <Heading.H5>
                      <FormattedMessage id="rewards.new.title" defaultMessage="Rewards Settings" />
                    </Heading.H5>
                  </Column>
                </Row>
                {showCompany && (
                  <Row>
                    <Column width="50%">
                      <CompanyAutocomplete
                        id="company.id"
                        onChange={handleCompanyChange}
                        onBlur={handleBlur}
                        value={values.company.name}
                        placeholder={intl.formatMessage(messages.companyPlaceholder)}
                        touched={touched.company && touched.company.id}
                        errors={errors.company && errors.company.id}
                        label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                      />
                    </Column>
                  </Row>
                )}
                <Row>
                  <Column width="50%">
                    <Input
                      id="rewardName"
                      value={values.rewardName}
                      onChange={handleChange}
                      placeholder={intl.formatMessage(messages.rewardNamePlaceholder)}
                      label={<FormattedMessage id="loyalty.rewards.new.name" defaultMessage="Reward Name" />}
                      onBlur={handleBlur}
                      touched={touched.rewardName}
                      errors={errors.rewardName}
                    />
                  </Column>

                  <Column width="50%">
                    <Select
                      id="rewardType"
                      value={values.rewardType}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                        setFieldValue('rewardType', e.target.value)
                      }}
                      label={<FormattedMessage id="loyalty.rewards.new.type" defaultMessage="Reward Type" />}
                      onBlur={handleBlur}
                      touched={touched.rewardType}
                      errors={errors.rewardType}
                    >
                      <option value="">{intl.formatMessage(messages.rewardTypeDefaultOption)}</option>
                      {REWARDS_TYPES_LIST.map((option) => {
                        const optionLabel = safeFormatMessage({
                          intl,
                          messages: rewardTypeMessages,
                          key: option
                        })
                        return (
                          <option value={option} key={option}>
                            {optionLabel}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Textarea
                      id="rewardDescription"
                      label={<FormattedMessage id="loyalty.rewards.new.description" defaultMessage="Description" />}
                      value={values.rewardDescription}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.rewardDescription}
                      errors={errors.rewardDescription}
                    />
                  </Column>
                </Row>
              </FormLayout.CardContent>
              <FormLayout.CardFooter>
                {error && (
                  <ErrorMessage>
                    <FormattedMessage id="login.submit.error" defaultMessage="An error has ocurred" />
                  </ErrorMessage>
                )}
                <FormLayout.CardFooterActions>
                  <Button as={NavLink} to="/loyalty/rewards" kind="cancel">
                    <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                  </Button>

                  <Button type="submit" kind="primary" loading={loading}>
                    <FormattedMessage id="dictionary.create" defaultMessage="Create" />
                  </Button>
                </FormLayout.CardFooterActions>
              </FormLayout.CardFooter>
            </FormLayout.Content>
          </form>
        )
      }}
    </Formik>
  )
}

export default withLastUrl(NewRewardView)
